import { FormEvent, useRef, useState, type CSSProperties } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTelegram } from "../hooks/useTelegram"
import { useRubberCanvas } from "../hooks/useRubberCanvas"
import { useI18n } from "../hooks/useI18n"
import type { MessageKey } from "../i18n"
import { RussianFlagCircle, SearchIcon } from "./MainFigmaVisuals"
import { openSupportBot } from "../utils/supportBot"
import "./CommercialPage.css"

const DIRECTIONS = {
  autotrade: "commercial.direction.autotrade",
  insurance: "commercial.direction.insurance",
  pitstop: "commercial.direction.pitstop",
  techhelp: "commercial.direction.techhelp",
} as const satisfies Record<string, MessageKey>

const COMMERCIAL_BODY_HEIGHT = 710

export function CommercialPage() {
  const { direction = "" } = useParams()
  const navigate = useNavigate()
  const { tg, hapticImpact } = useTelegram()
  const { t } = useI18n()
  const cityInputRef = useRef<HTMLInputElement>(null)
  const directionKey = DIRECTIONS[direction as keyof typeof DIRECTIONS]
  const title = directionKey ? t(directionKey) : undefined
  const [city, setCity] = useState("")
  const [searched, setSearched] = useState(false)
  const [cityFocused, setCityFocused] = useState(false)
  const isIOS = ["ios", "macos"].includes((tg?.platform || "").toLowerCase())
  const { fitAreaRef, scale, visualWidth, canvasWidth } = useRubberCanvas(COMMERCIAL_BODY_HEIGHT, 24)

  if (!title) return null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    hapticImpact("light")
    if (city.trim().length >= 2) setSearched(true)
  }

  const openPartnerBot = () => {
    openSupportBot(tg, direction as keyof typeof DIRECTIONS)
  }

  return (
    <main className={`commercial-final ${isIOS ? "platform-ios" : "platform-android"}`}>
      <header className="commercial-header">
        <button type="button" onClick={() => navigate(-1)}>{t("common.back")}</button>
        <h1>{title}</h1>
      </header>

      <div className="commercial-fit-area" ref={fitAreaRef}>
        <div
          className="commercial-scale-box"
          style={{ width: `${visualWidth}px`, height: `${COMMERCIAL_BODY_HEIGHT * scale}px` }}
        >
          <div
            className="commercial-canvas"
            style={{
              width: `${canvasWidth}px`,
              height: `${COMMERCIAL_BODY_HEIGHT}px`,
              transform: `scale(${scale})`,
              "--commercial-edge-24": `${24 / scale}px`,
              "--commercial-edge-32": `${32 / scale}px`,
            } as CSSProperties}
          >
            <form onSubmit={submit}>
              <label>{t("commercial.searchLabel")}</label>
              <div className="commercial-search">
                <button
                  type="button"
                  className="commercial-search-country"
                  aria-label={t("country.russia")}
                  onClick={() => cityInputRef.current?.focus()}
                >
                  <RussianFlagCircle />
                </button>
                <input
                  ref={cityInputRef}
                  placeholder={cityFocused ? "" : t("commercial.cityPlaceholder")}
                  value={city}
                  enterKeyHint="search"
                  onFocus={() => setCityFocused(true)}
                  onBlur={() => setCityFocused(false)}
                  onChange={(event) => {
                    setCity(event.target.value)
                    setSearched(false)
                  }}
                />
                <button type="submit" className="commercial-search-action" aria-label={t("common.search")}>
                  <SearchIcon />
                </button>
              </div>
            </form>

            {searched ? (
              <section className="commercial-empty">
                <h2>{t("commercial.emptyTitle")}</h2>
                <p>
                  {t("commercial.emptyLine1")}<br />
                  {t("commercial.emptyLine2")}<br />
                  {t("commercial.emptyLine3")}
                </p>
              </section>
            ) : (
              <p className="commercial-helper">{t("commercial.helperLine1")}<br />{t("commercial.helperLine2")}</p>
            )}

            <footer>
              <p>{t("commercial.partnerPrompt")}</p>
              <button type="button" onClick={openPartnerBot}>{t("commercial.apply")}</button>
            </footer>
          </div>
        </div>
      </div>
    </main>
  )
}
